import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Breadcrumbs, Link, Grid, Card, CardContent,
    CardActionArea, Button, Skeleton, Chip, CardMedia, LinearProgress,
    Tooltip, Paper, Avatar, Fade
} from '@mui/material';
import api from '../../services/api';
import { Category, Course } from '../../types/course';
import { designTokens } from '../../theme/designTokens';
import SchoolIcon from '@mui/icons-material/School';
import CategoryIcon from '@mui/icons-material/Category';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { decodeHTMLEntities } from '../../utils/stringUtils';


const LearnerCategoryDetails = () => {
    const { categoryId } = useParams<{ categoryId: string }>();
    const [category, setCategory] = useState<Category | null>(null);
    const [courseProgress, setCourseProgress] = useState<Map<string, number>>(new Map());
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        fetchDetails();
        fetchCourseProgress();
    }, [categoryId]);

    const fetchDetails = async () => {
        try {
            const res = await api.get('/api/course/parcours');
            let foundCategory: Category | null = null;

            const findCategory = (cats: Category[], targetId: string): Category | null => {
                for (const cat of cats) {
                    if (cat._id === targetId) return cat;
                    if (cat.categories) {
                        const found = findCategory(cat.categories, targetId);
                        if (found) return found;
                    }
                }
                return null;
            };

            for (const p of res.data) {
                if (p.blocs) {
                    for (const b of p.blocs) {
                        if (b.categories) {
                            const found = findCategory(b.categories, categoryId || '');
                            if (found) {
                                foundCategory = found;
                                break;
                            }
                        }
                    }
                }
                if (foundCategory) break;
            }

            setCategory(foundCategory);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching details", err);
            setLoading(false);
        }
    };

    const fetchCourseProgress = async () => {
        try {
            const res = await api.get('/api/progress/courses-progress');
            const progressMap = new Map<string, number>();
            res.data.forEach((item: any) => {
                progressMap.set(item.courseId, item.progress);
            });
            setCourseProgress(progressMap);
        } catch (err) {
            console.error("Error fetching course progress", err);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
                <Skeleton variant="text" width={300} height={40} sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                    {[1, 2, 3].map((i) => (
                        <Grid size={{ xs: 12 }} key={i}>
                            <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 4 }} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    if (!category) return <Box sx={{ p: 4 }}>Catégorie introuvable</Box>;

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
            p: { xs: 2, md: 4 }
        }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
                <Link
                    component={RouterLink}
                    underline="hover"
                    color="inherit"
                    to="/learner/parcours"
                    sx={{ display: 'flex', alignItems: 'center' }}
                >
                    <ArrowBackIcon sx={{ mr: 0.5, fontSize: 18 }} /> Mes Parcours
                </Link>
                <Typography color="text.primary" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                    {decodeHTMLEntities(category.title)}
                </Typography>
            </Breadcrumbs>

            {/* Hero Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 4,
                    p: { xs: 3, md: 5 },
                    mb: 5,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 180,
                    height: 180,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                }} />
                <Box sx={{
                    position: 'absolute',
                    bottom: -30,
                    right: 80,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.08)',
                }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Avatar sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        width: 56,
                        height: 56,
                        backdropFilter: 'blur(10px)'
                    }}>
                        <CategoryIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: 'Quicksand, sans-serif', fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                            {decodeHTMLEntities(category.title)}
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                            {category.courses?.length && category.courses?.length > 0
                                ? `${category.courses?.length} Cours disponibles`
                                : category.categories?.length && category.categories?.length > 0
                                    ? `${category.categories?.length} Sous-catégories disponibles`
                                    : 'Accédez à vos contenus de formation'}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {category.categories && category.categories.length > 0 && (
                <Box sx={{ mb: 5 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 800, color: '#2c3e50', fontFamily: 'Quicksand, sans-serif', mb: 3 }}>
                        Sous-catégories
                    </Typography>
                    <Grid container spacing={3}>
                        {category.categories.map((subCat, index) => (
                            <Grid size={{ xs: 12, md: 6 }} key={subCat._id}>
                                <Fade in timeout={300 + index * 100}>
                                    <Card sx={{
                                        ...designTokens.glassCard,
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        border: '1px solid rgba(102, 126, 234, 0.1)',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 12px 40px rgba(102, 126, 234, 0.2)',
                                            bgcolor: 'rgba(255, 255, 255, 0.95)'
                                        }
                                    }}>
                                        <CardActionArea
                                            onClick={() => navigate(`/learner/category/${subCat._id}`)}
                                            sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', p: 0 }}
                                        >
                                            {subCat.imageUrl ? (
                                                <CardMedia
                                                    component="img"
                                                    height="160"
                                                    image={subCat.imageUrl}
                                                    alt={subCat.title}
                                                    sx={{ objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <Box sx={{
                                                    height: 160,
                                                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: '#667eea'
                                                }}>
                                                    <CategoryIcon sx={{ fontSize: 56, opacity: 0.5 }} />
                                                </Box>
                                            )}
                                            <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#2c3e50', fontFamily: 'Quicksand, sans-serif', mb: 2 }}>
                                                    {decodeHTMLEntities(subCat.title)}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip
                                                        label={`${subCat.courses?.length || 0} Cours`}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: 'rgba(102, 126, 234, 0.08)',
                                                            color: '#667eea',
                                                            fontWeight: 700,
                                                            borderRadius: 1.5
                                                        }}
                                                    />
                                                </Box>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Fade>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {category.courses && category.courses.length > 0 && (
                <>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 800, color: '#2c3e50', fontFamily: 'Quicksand, sans-serif', mb: 3 }}>
                        Contenus de formation
                    </Typography>
                    <Grid container spacing={3}>
                        {category.courses?.map((course, index) => (
                            <Grid size={{ xs: 12 }} key={course._id}>
                                <Fade in timeout={300 + index * 50}>
                                    <Card sx={{
                                        ...designTokens.glassCard,
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        border: '1px solid rgba(102, 126, 234, 0.1)',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.15)',
                                            bgcolor: 'rgba(255, 255, 255, 0.95)'
                                        }
                                    }}>
                                        <CardActionArea
                                            onClick={() => window.open(`/learner/course/${course._id}`, '_blank')}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                alignItems: { xs: 'stretch', sm: 'center' },
                                                p: 3,
                                                justifyContent: 'flex-start'
                                            }}
                                        >
                                            <Box sx={{
                                                p: 2.5,
                                                borderRadius: 3,
                                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                                                color: '#667eea',
                                                mr: { xs: 0, sm: 3 },
                                                mb: { xs: 2, sm: 0 },
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <SchoolIcon fontSize="large" />
                                            </Box>
                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#2c3e50', fontFamily: 'Quicksand, sans-serif' }}>
                                                        {decodeHTMLEntities(course.title)}
                                                    </Typography>
                                                    {courseProgress.has(course._id) && courseProgress.get(course._id)! >= 100 && (
                                                        <CheckCircleIcon sx={{ color: '#10b981', fontSize: 24 }} />
                                                    )}
                                                    {course.duration && (
                                                        <Tooltip title="Durée moyenne estimée" arrow>
                                                            <Chip
                                                                icon={<AccessTimeIcon sx={{ fontSize: '1rem !important' }} />}
                                                                label={course.duration}
                                                                size="small"
                                                                sx={{
                                                                    height: 24,
                                                                    bgcolor: 'rgba(102, 126, 234, 0.08)',
                                                                    color: '#667eea',
                                                                    fontWeight: 700,
                                                                    borderRadius: 1.5,
                                                                    '& .MuiChip-icon': { color: '#667eea' }
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        fontFamily: 'Quicksand, sans-serif',
                                                        mb: 2,
                                                        lineHeight: 1.6,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden'
                                                    }}
                                                    dangerouslySetInnerHTML={{ __html: course.description || "Aucune description disponible." }}
                                                />
                                                {courseProgress.has(course._id) && courseProgress.get(course._id)! > 0 && (
                                                    <Box sx={{ mt: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                                                Votre progression
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ fontWeight: 800, color: courseProgress.get(course._id)! >= 100 ? '#10b981' : '#667eea' }}>
                                                                {Math.min(100, Math.round(courseProgress.get(course._id)!))}%
                                                            </Typography>
                                                        </Box>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={Math.min(100, courseProgress.get(course._id)!)}
                                                            sx={{
                                                                height: 8,
                                                                borderRadius: 4,
                                                                bgcolor: 'rgba(0,0,0,0.05)',
                                                                '& .MuiLinearProgress-bar': {
                                                                    borderRadius: 4,
                                                                    background: courseProgress.get(course._id)! >= 100
                                                                        ? 'linear-gradient(90deg, #10b981 0%, #34d399 100%)'
                                                                        : 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                            <Button
                                                variant="contained"
                                                endIcon={<PlayCircleOutlineIcon />}
                                                sx={{
                                                    ml: { xs: 0, sm: 4 },
                                                    mt: { xs: 2, sm: 0 },
                                                    borderRadius: 2.5,
                                                    px: 3,
                                                    py: 1,
                                                    textTransform: 'none',
                                                    fontFamily: 'Quicksand, sans-serif',
                                                    fontWeight: 700,
                                                    background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                                                    boxShadow: '0 4px 14px 0 rgba(102, 126, 234, 0.39)',
                                                    whiteSpace: 'nowrap',
                                                    flexShrink: 0,
                                                    minWidth: '145px',
                                                    '&:hover': {
                                                        transform: 'scale(1.02)',
                                                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.23)'
                                                    }
                                                }}
                                            >
                                                {courseProgress.get(course._id)! >= 100 ? 'Revoir' : courseProgress.get(course._id)! > 0 ? 'Continuer' : 'Commencer'}
                                            </Button>
                                        </CardActionArea>
                                    </Card>
                                </Fade>
                            </Grid>
                        ))}
                    </Grid>
                </>)}
            {((!category.courses || category.courses.length === 0) && (!category.categories || category.categories.length === 0)) && (
                <Grid size={{ xs: 12 }}>
                    <Paper sx={{
                        textAlign: 'center',
                        py: 10,
                        borderRadius: 4,
                        background: 'rgba(255,255,255,0.5)',
                        border: '2px dashed #e0e0e0',
                    }}>
                        <SchoolIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ fontFamily: 'Quicksand, sans-serif' }}>
                            Aucun contenu n'est encore disponible dans cette catégorie.
                        </Typography>
                    </Paper>
                </Grid>
            )}
        </Box>
    );
};

export default LearnerCategoryDetails;

